import './styles/producttracker.css';
import Footer from '@/shared/components/footer/footer';
import Header from '@/pages/index/components/Header/Header';
import OrderTrackerHeader from './OrderTrackerHeader/OrderTrackerHeader';
import OrderTrackerHeaderMobile from './OrderTrackerHeader/OrderTrackerHeaderMobile';
import RiderDetails from './RiderDetails/RiderDetails';
import RiderDetailsMobile from './RiderDetails/RiderDetailsMobile';
import OrderProgressVertical from './OrderProgressVertical/OrderProgressVertical';
import OrderProgressVerticalM from './OrderProgressVertical/OrderProgressVerticalM';
import DeliveryMap from './DeliveryMap/DeliveryMap';
import OrderItemsCard from './OrderItemsCard/OrderItemsCard';
import OrderItemsCardMobile from './OrderItemsCard/OrderItemsCardMobile';

import { useParams, useNavigate } from 'react-router-dom';
import React from 'react';
import axios from 'axios';
import { ApiService } from '@/shared/service/Apiservice';
import { TrackingService } from './service/Tracking.service';
import { ProfileService } from '@/pages/Profile/service/Profile.service';
import MobileFooter from '@/shared/components/MobileFooter/MobileFooter';
import NavigationMobileMenu from '@/shared/components/NavigationMobileMenu/NavigationMobileMenu';


// Custom hook to detect screen sizes
const useScreenDetect = () => {
  const [screenSize, setScreenSize] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  React.useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width <= 540) {
        setScreenSize('mobile');
      } else if (width > 540 && width < 1280) {
        // Tablet sizes: 768, 820, 853, 912, 1024, 1180
        setScreenSize('tablet');
      } else {
        // Desktop/Laptop: 1280 and above
        setScreenSize('desktop');
      }
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return screenSize;
};

const ProductTracker = () => {
  const navigate = useNavigate();
  const screenSize = useScreenDetect();
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const { orderId } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tracking, setTracking] = React.useState<any | null>(null);
  const [order, setOrder] = React.useState<any | undefined>();


  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const trackingService = React.useMemo(() => new TrackingService(apiService), [apiService]);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);

  React.useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        if (!orderId) {
          throw new Error('No orderId provided');
        }
        const [trackingResp, orderResp] = await Promise.all([
          trackingService.getTrackingInfo(orderId),
          profileService.GetUserOrderById(orderId)
        ]);
        if (!mounted) return;
        setTracking(trackingResp);
        // order service returns ApiResponse
        if ((orderResp as any).success && (orderResp as any).data) {
          setOrder((orderResp as any).data);
        } else if ((orderResp as any).success && (orderResp as any).order) {
          setOrder((orderResp as any).order);
        } else {
          // Some endpoints might return the order directly
          setOrder(orderResp as any);
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load tracking info');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [orderId, trackingService, profileService]);



  
  // For tablet (540-1280px), show layout like in the image
  if (isTablet) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Header />
          <div className="order-tracker-header-wrapper">
            {!loading && !error && order && tracking && (
              <OrderTrackerHeader
                orderId={order._id || 'N/A'}
                orderPlacedAt={order.createdAt || new Date().toISOString()}
                paymentMode={order.paymentMode || 'N/A'}
                estimatedDeliveryTime={
                  order.status?.toLowerCase() === 'cancelled'
                    ? 'Cancelled'
                    : tracking.status?.toLowerCase?.() === 'delivered'
                    ? 'Arrived'
                    : tracking.estimatedDeliveryTime
                      ? `Arriving in ${Math.round((new Date(tracking.estimatedDeliveryTime).getTime() - new Date().getTime()) / 60000)} minutes`
                      : undefined
                }
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '1rem', gap: '1rem', boxSizing: 'border-box' }}>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading tracking...</div>
            ) : error ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#dc3545' }}>{error}</div>
            ) : tracking ? (
              <>
                {/* Left side - Order Progress */}
                <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                  <div style={{ flex: '0 0 200px', maxWidth: '200px' }}>
                    <div className="product-tracker-card">
                      <OrderProgressVertical
                        trackingHistory={tracking.trackingHistory || []}
                        orderStatus={tracking.status}
                        createdAt={order?.createdAt}
                      />
                    </div>
                  </div>

                  {/* Right side - Map and other cards */}
                  <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 0 }}>
                    {/* Map */}
                    <div className="product-tracker-card" style={{ width: '100%', height: '300px', minHeight: '300px' }}>
                      <DeliveryMap
                        warehouseLocation={(order as any)?.warehouseLocation}
                        userLocation={(order as any)?.userLocation}
                        warehouseName={(order as any)?.warehouse?.name || "Warehouse"}
                        deliveryAddress={(order as any)?.shippingAddress?.primary_address_line}
                      />
                    </div>

                    {/* Rider Details and Order Items side by side */}
                    <div className="items-and-rider-wrapper">
                      {/* Order Items */}
                      {!loading && !error && order && (
                        <div className="product-tracker-card">
                          <OrderItemsCard
                            items={order.items || []}
                            orderId={order.orderId || order._id}
                            totalAmount={order.totalAmount}
                            subtotal={order.subtotal}
                            shippingCost={order.shippingCost}
                            totalGST={order.totalGST}
                            couponDiscount={order.couponDiscount}
                            appliedCredits={order.appliedCredits}
                          />
                        </div>
                      )}

                      {/* Rider Details */}
                      <div className="product-tracker-card">
                        <RiderDetails
                          riderName={tracking.deliveryAgent?.name}
                          riderPhone={tracking.deliveryAgent?.phone}
                          vehicleNumber={tracking.deliveryAgent?.licenseNumber || order.licenseNumber || order.vehicle || tracking.deliveryAgent?.vehicle}
                          orderId={order._id}
                          orderStatus={order.status}
                          trackingStatus={tracking.status}
                          onOrderCancelled={() => {
                            if (orderId) {
                              Promise.all([
                                trackingService.getTrackingInfo(orderId),
                                profileService.GetUserOrderById(orderId)
                              ]).then(([trackingResp, orderResp]) => {
                                setTracking(trackingResp);
                                if ((orderResp as any).success && (orderResp as any).data) {
                                  setOrder((orderResp as any).data);
                                } else if ((orderResp as any).success && (orderResp as any).order) {
                                  setOrder((orderResp as any).order);
                                } else {
                                  setOrder(orderResp as any);
                                }
                              }).catch(err => {
                                console.error('Failed to reload order:', err);
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* MobileFooter for all tablet sizes */}
        <MobileFooter />
        <NavigationMobileMenu />
      </div>
    );
  }

  // Original mobile and desktop layouts
  return (
    <>
      {!isMobile && <Header />}
      {!isMobile && (
        <div className="order-tracker-header-wrapper">
          {!loading && !error && order && tracking && (
            <OrderTrackerHeader
              orderId={order._id || 'N/A'}
              orderPlacedAt={order.createdAt || new Date().toISOString()}
              paymentMode={order.paymentMode || 'N/A'}
              estimatedDeliveryTime={(() => {
                if (order.status?.toLowerCase() === 'cancelled') return 'Cancelled';
                if (tracking.status?.toLowerCase?.() === 'delivered') return 'Arrived';

                // Scheduled delivery: show the booked time window
                if (
                  order.deliveryType === 'scheduled' &&
                  order.scheduledDeliveryDate &&
                  order.scheduledDeliveryTime
                ) {
                  const [start, end] = order.scheduledDeliveryTime.split('-');
                  const datePart = new Date(order.scheduledDeliveryDate);
                  const formatTime = (time: string) => {
                    const [h, m] = time.split(':').map(Number);
                    const d = new Date(datePart);
                    d.setHours(h || 0, m || 0, 0, 0);
                    return d.toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    });
                  };
                  const startLabel = formatTime(start);
                  const endLabel = formatTime(end);
                  return `Scheduled for ${startLabel} - ${endLabel}`;
                }

                // Custom ETA logic based on status
                const status = (tracking.status || '').toLowerCase();
                if (status === 'order confirmed') {
                  return 'Arriving in 30 minutes';
                } else if (status === 'preparing') {
                  return 'Arriving in 25 minutes';
                } else if (status === 'dispatched') {
                  return 'Arriving in 15 minutes';
                } else if (tracking.estimatedDeliveryTime) {
                  let diffMinutes = Math.round(
                    (new Date(tracking.estimatedDeliveryTime).getTime() - new Date().getTime()) / 60000
                  );
                  if (diffMinutes === 60) diffMinutes = 30;
                  return diffMinutes > 0
                    ? `Arriving in ${diffMinutes} minutes`
                    : undefined;
                }

                return undefined;
              })()}
            />
          )}
        </div>
      )}

      {isMobile && !loading && !error && order && (
        <OrderTrackerHeaderMobile
          orderId={order._id || 'N/A'}
          onBack={() => navigate('/profile')}
        />
      )}

      <div className="product-tracker-layout">
          <div className="tracker-wrapper">
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading tracking...</div>
            ) : error ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#dc3545' }}>{error}</div>
            ) : tracking ? (
              <>
                <div className="product-tracker-card">
                  {isMobile ? (
                    <OrderProgressVerticalM
                      trackingHistory={tracking.trackingHistory || []}
                      orderStatus={tracking.status}
                      createdAt={order?.createdAt}
                      estimatedDelivery={tracking.estimatedDeliveryTime}
                      deliveryType={order?.deliveryType}
                      scheduledDeliveryDate={order?.scheduledDeliveryDate}
                      scheduledDeliveryTime={order?.scheduledDeliveryTime}
                      paymentMode={order?.paymentMode}
                    />
                  ) : (
                    <OrderProgressVertical
                      trackingHistory={tracking.trackingHistory || []}
                      orderStatus={tracking.status}
                      createdAt={order?.createdAt}
                    />
                  )}
                </div>

                {isMobile && (
                  <div className="product-tracker-card map-card-mobile">
                    <DeliveryMap
                      warehouseLocation={(order as any)?.warehouseLocation}
                      userLocation={(order as any)?.userLocation}
                      warehouseName={(order as any)?.warehouse?.name || "Warehouse"}
                      deliveryAddress={(order as any)?.shippingAddress?.primary_address_line}
                    />
                  </div>
                )}

                {isMobile && (
                  <div className="items-and-rider-wrapper">
                    <RiderDetailsMobile
                      riderName={tracking.deliveryAgent?.name}
                      riderPhone={tracking.deliveryAgent?.phone}
                      vehicleNumber={tracking.deliveryAgent?.licenseNumber || order.licenseNumber || order.vehicle || tracking.deliveryAgent?.vehicle}
                      orderId={order._id}
                      orderStatus={order.status}
                      trackingStatus={tracking.status}
                      onOrderCancelled={() => {
                        if (orderId) {
                          Promise.all([
                            trackingService.getTrackingInfo(orderId),
                            profileService.GetUserOrderById(orderId)
                          ]).then(([trackingResp, orderResp]) => {
                            setTracking(trackingResp);
                            if ((orderResp as any).success && (orderResp as any).data) {
                              setOrder((orderResp as any).data);
                            } else if ((orderResp as any).success && (orderResp as any).order) {
                              setOrder((orderResp as any).order);
                            } else {
                              setOrder(orderResp as any);
                            }
                          }).catch(err => {
                            console.error('Failed to reload order:', err);
                          });
                        }
                      }}
                    />
                    {!loading && !error && order && (
                      <OrderItemsCardMobile
                        items={order.items || []}
                        orderId={order.orderId || order._id}
                        totalAmount={order.totalAmount}
                        subtotal={order.subtotal}
                        shippingCost={order.shippingCost}
                        totalGST={order.totalGST}
                        couponDiscount={order.couponDiscount}
                        appliedCredits={order.appliedCredits}
                      />
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>

          {!isMobile && (
            <div className="map-wrapper">
              {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading map...</div>
              ) : error ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#dc3545' }}>{error}</div>
              ) : tracking ? (
                <>
                  <div className="product-tracker-card">
                    <DeliveryMap
                      warehouseLocation={(order as any)?.warehouseLocation}
                      userLocation={(order as any)?.userLocation}
                      warehouseName={(order as any)?.warehouse?.name || "Warehouse"}
                      deliveryAddress={(order as any)?.shippingAddress?.primary_address_line}
                    />
                  </div>
                  <div className="items-and-rider-wrapper">
                    {!loading && !error && order && (
                      <OrderItemsCard
                        items={order.items || []}
                        orderId={order.orderId || order._id}
                        totalAmount={order.totalAmount}
                        subtotal={order.subtotal}
                        shippingCost={order.shippingCost}
                        totalGST={order.totalGST}
                        couponDiscount={order.couponDiscount}
                        appliedCredits={order.appliedCredits}
                      />
                    )}
                    <RiderDetails
                      riderName={tracking.deliveryAgent?.name}
                      riderPhone={tracking.deliveryAgent?.phone}
                      vehicleNumber={tracking.deliveryAgent?.licenseNumber || order.licenseNumber || order.vehicle || tracking.deliveryAgent?.vehicle}
                      orderId={order._id}
                      orderStatus={order.status}
                      trackingStatus={tracking.status}
                      onOrderCancelled={() => {
                        if (orderId) {
                          Promise.all([
                            trackingService.getTrackingInfo(orderId),
                            profileService.GetUserOrderById(orderId)
                          ]).then(([trackingResp, orderResp]) => {
                            setTracking(trackingResp);
                            if ((orderResp as any).success && (orderResp as any).data) {
                              setOrder((orderResp as any).data);
                            } else if ((orderResp as any).success && (orderResp as any).order) {
                              setOrder((orderResp as any).order);
                            } else {
                              setOrder(orderResp as any);
                            }
                          }).catch(err => {
                            console.error('Failed to reload order:', err);
                          });
                        }
                      }}
                    />
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>

      {!isMobile && <Footer/>}
      {isMobile && <MobileFooter/>}
      {isMobile && <NavigationMobileMenu />}




    </>
  );
};

export default ProductTracker;